import { useCallback, useEffect, useRef, useState } from 'react';

import { IServices, PlaceSuggestion } from '../../../../../types';
import { MappedPlace, mapPlaceDetails } from '../helpers/mapPlaceDetails';

type Params = {
  services: Pick<IServices, 'placesAutocomplete' | 'placeDetails'>;
  regionCode: string;
  enabled: boolean;
};

export const MIN_QUERY_LENGTH = 3;

const newSessionToken = (): string => {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

/**
 * Google Places (New) autocomplete state for the "Find Your Address" field.
 *
 * - One session token spans all keystrokes + the final Place Details call and
 *   is rotated after `resolve` (billing: session ≈ free autocomplete).
 * - In-flight autocomplete requests are aborted when a newer query starts.
 * - Disabled (preview / flag off) → every call is a no-op.
 */
export const usePlacesAutocomplete = ({
  services,
  regionCode,
  enabled,
}: Params) => {
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const sessionToken = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const getSessionToken = (): string => {
    if (!sessionToken.current) {
      sessionToken.current = newSessionToken();
    }
    return sessionToken.current;
  };

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const clear = useCallback(() => {
    abortRef.current?.abort();
    setSuggestions([]);
    setLoading(false);
  }, []);

  const search = useCallback(
    async (input: string) => {
      setError(null);
      const query = input.trim();

      if (!enabled || query.length < MIN_QUERY_LENGTH) {
        clear();
        return;
      }

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);

      try {
        const result = await services.placesAutocomplete({
          input: query,
          regionCode,
          sessionToken: getSessionToken(),
          signal: controller.signal,
        });
        if (!controller.signal.aborted) {
          setSuggestions(result);
        }
      } catch (e) {
        if ((e as Error)?.name === 'AbortError') {
          return;
        }
        setError(e as Error);
        setSuggestions([]);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    },
    [enabled, regionCode, services, clear],
  );

  const resolve = useCallback(
    async (placeId: string): Promise<MappedPlace> => {
      const details = await services.placeDetails({
        placeId,
        sessionToken: getSessionToken(),
      });
      // Session ends with the details call — next keystroke opens a new one.
      sessionToken.current = null;
      return mapPlaceDetails(details?.addressComponents);
    },
    [services],
  );

  return { suggestions, loading, error, search, resolve, clear };
};
